import InfoCallout from "@components/common/info/InfoCallout";
import { KeyStrokeTextDecorator } from "@components/common/info/TextDecorators";
import { faTurnDownLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField, Chip, Autocomplete } from "@mui/material";
import { useState } from "react";

import { alert } from "../Alert";

export const isValidEmail = (
  email,
  existingEmails = [],
  customValidation = null,
) => {
  if (existingEmails?.includes(email)) {
    alert("warning", `${email} is already included.`);
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    alert("error", `${email} is not a valid email.`);
    return false;
  }

  if (customValidation && !customValidation(email)) {
    return false;
  }

  return true;
};

const MultiEmailInput = ({
  emails,
  setEmails,
  placeholder = "Add email addresses",
  infoText = "Type in email addresses, then press Enter to add them to the list.",
  customValidation = null,
  existingEmails = [],
  disabled = false,
  dataTestId = "multi-email-input",
}) => {
  const [inputValue, setInputValue] = useState("");
  const users = emails || [];

  const textFieldStyles = {
    width: "100%",
    resize: "none",
    appearance: "none",
    border: "2px solid",
    borderColor: "var(--border-input)",
    backgroundColor: "var(--surface-input)",
    color: "var(--text-primary)",
    borderRadius: "var(--ds-radius-standard)",
    boxShadow: "0px 0px 10px 0px var(--shadow-primary)",
    transition: "all 150ms ease",
    "& .MuiInputBase-input": {
      color: "var(--text-primary)",
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "transparent",
        },
      },
    },
    "&:hover": {
      boxShadow: "0px 0px 10px 0px var(--shadow-focus)",
    },
    "&:focus-within": {
      boxShadow: "0px 0px 10px 0px var(--shadow-focus)",
      borderColor: "var(--border-brand)",
    },
    "& .MuiAutocomplete-clearIndicator": {
      color: "var(--text-primary)",
    },
    "&::placeholder": {
      color: "var(--text-placeholder)",
    },
    "&:focus": {
      outline: "none",
      zIndex: 10,
    },
  };

  const chipStyles = {
    margin: "1px",
    borderColor: "var(--border-secondary)",
    backgroundColor: "var(--surface-secondary)",
    color: "var(--text-primary)",
    "&:hover": {
      borderColor: "var(--border-brand)",
    },
    "& .MuiChip-deleteIcon": {
      color: "var(--text-placeholder)",
      "&:hover": {
        color: "var(--text-brand)",
      },
    },
  };

  const handleAddUser = (_, newValue) => {
    const validEmails = (newValue || []).filter((email) =>
      isValidEmail(email, existingEmails || [], customValidation),
    );

    if (typeof setEmails === "function") {
      setEmails(validEmails);
    } else {
      console.error("setEmails is not a function:", setEmails);
    }
  };

  const handleBlur = () => {
    if ((users || []).includes(inputValue.trim())) {
      return false;
    }
    if (inputValue.trim()) {
      const allExistingEmails = [
        ...(existingEmails || []),
        ...(users || []),
      ];
      if (
        isValidEmail(inputValue, allExistingEmails, customValidation)
      ) {
        const updatedUsers = [...(users || []), inputValue];
        if (typeof setEmails === "function") {
          setEmails(updatedUsers);
        } else {
          console.error("setEmails is not a function:", setEmails);
        }
        setInputValue("");
      }
    }
  };

  return (
    <>
      <Autocomplete
        size="small"
        multiple
        freeSolo
        disabled={disabled}
        options={[]}
        value={users || []}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            (users || []).includes(event.target.value)
          ) {
            alert("error", "You have already included this email.");
          }
        }}
        onChange={handleAddUser}
        onBlur={handleBlur}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              key={option}
              sx={chipStyles}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            sx={textFieldStyles}
            data-testid={dataTestId}
          />
        )}
      />
      <InfoCallout>
        {infoText}
        <br />
        <KeyStrokeTextDecorator>
          <FontAwesomeIcon icon={faTurnDownLeft} /> Enter
        </KeyStrokeTextDecorator>{" "}
        to add each email to the list.
      </InfoCallout>
    </>
  );
};

export default MultiEmailInput;
